import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Box, CircularProgress, Alert, Button, Container } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useTaxBinder } from '@/hooks/api/useTaxBinders';
import { useTaxBinderTaxReturns } from '@/hooks/api/useTaxBinderTaxReturns';
import { useBinderVerificationStatus } from '@/hooks/api/useTaxBinderVerification';
import { GuidedSigningWizard } from '@/features/tax-binders/signing-wizard';
import { ClientCopyReviewDialog } from '@/features/tax-binders/components/ClientCopyReviewDialog';
import { useClientCopyReview } from '@/features/tax-binders/hooks/useClientCopyReview';
import { taxReturnQueryOptions, taxBinderQueryOptions } from '@/lib/query/options';
import { QueryErrorBoundary } from '@/components/query/QueryErrorBoundary';

export const Route = createFileRoute('/tax-binders/$binderId/sign-returns')({
  loader: async ({ context: { queryClient }, params: { binderId } }) => {
    // Get auth state from query cache (client auth)
    const authState = queryClient.getQueryData(['auth', 'session']) as { token?: string } | undefined;
    const token = authState?.token;

    if (token && binderId) {
      // Prefetch binder and tax returns in parallel
      await Promise.all([
        queryClient.ensureQueryData(taxBinderQueryOptions.detail(binderId, token)),
        queryClient.ensureQueryData(taxReturnQueryOptions.list(binderId, token)),
      ]);
    }
  },
  errorComponent: QueryErrorBoundary,
  component: SignReturnsPage,
});

/** Accepting updates the cached review, which drops the guard on the next render. */
const noop = () => {};

/**
 * Sign Returns Page
 *
 * Guided wizard for signing all pending tax return authorizations (Form 8879).
 * Supports multi-signer scenarios with sequential signing.
 */
function SignReturnsPage() {
  const { binderId } = Route.useParams();
  const navigate = useNavigate();

  const { data: binder, isLoading: isLoadingBinder, error: binderError } = useTaxBinder(binderId);
  const { data: taxReturns, isLoading: isLoadingReturns, error: returnsError } = useTaxBinderTaxReturns(binderId);
  const { data: verificationStatus, isLoading: isLoadingVerification } = useBinderVerificationStatus(binderId);
  const {
    data: clientCopy,
    isLoading: isLoadingClientCopy,
    isError: isClientCopyError,
  } = useClientCopyReview(binderId);

  const handleBack = () => {
    navigate({
      to: '/tax-binders/$binderId/returns',
      params: { binderId },
    });
  };

  const handleGoToVerification = () => {
    navigate({
      to: '/tax-binders/$binderId',
      params: { binderId },
    });
  };

  // Loading state
  if (isLoadingBinder || isLoadingReturns || isLoadingVerification || isLoadingClientCopy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Spouse-contact gate: a released MFJ binder whose spouse signer still has no
  // account/email must go back through spouse verification before anyone signs.
  // The progress tracker already re-opens that step, but this route is directly
  // reachable — block it here too so the primary can't sign around the gate.
  if (verificationStatus?.requiresSpouseContactInfo) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Before signing can begin, please provide your spouse&apos;s contact information so we can send them their copy
          to sign.
        </Alert>
        <Button variant="contained" onClick={handleGoToVerification}>
          Complete spouse verification
        </Button>
      </Container>
    );
  }

  // Client-copy gate: this route is directly reachable, so the CTA check alone would let a signer
  // navigate around reading the return. The server refuses a signing slug regardless.
  if (clientCopy && !clientCopy.acknowledgedByCurrentUser) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          Please review your tax return before signing.
        </Alert>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={handleBack}>
          Return to Tax Returns
        </Button>
        <ClientCopyReviewDialog open binderId={binderId} onClose={handleBack} onAccepted={noop} />
      </Container>
    );
  }

  // Error state
  if (binderError || returnsError || isClientCopyError || !binder) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load tax return information. Please try again.
        </Alert>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={handleBack}>
          Return to Tax Returns
        </Button>
      </Container>
    );
  }

  // No returns to sign
  if (!taxReturns || taxReturns.length === 0) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          There are no tax returns requiring your signature at this time.
        </Alert>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={handleBack}>
          Return to Tax Returns
        </Button>
      </Container>
    );
  }

  // Render the wizard
  return <GuidedSigningWizard binderId={binderId} binder={binder} taxReturns={taxReturns} />;
}
